import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, X } from 'lucide-react';
import { offlineSync, SyncStatus } from '../utils/offlineSync';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { offlineDataCache, CACHE_KEYS } from '../utils/offlineDataCache';

export const OfflineBanner: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(offlineSync.getStatus());
  const [isVisible, setIsVisible] = useState(false);
  const [isPremium, setIsPremium] = useState<boolean>(false);

  const hasImpersonationBanner = user?.impersonating;

  // Check premium status
  useEffect(() => {
    const checkPremium = () => {
      const cached = offlineDataCache.get<any>(CACHE_KEYS.SUBSCRIPTION);
      if (cached) {
        setIsPremium(cached.plan === 'premium');
      } else if (navigator.onLine) {
        // Try to fetch subscription if online
        const token = localStorage.getItem('token');
        const accountId = user?.currentAccountId || user?.accountId;
        if (token && accountId) {
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/subscriptions/current`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(data => {
              if (data.success && data.data?.subscription) {
                const subscription = data.data.subscription;
                setIsPremium(subscription.plan === 'premium');
                offlineDataCache.set(CACHE_KEYS.SUBSCRIPTION, subscription);
              } else {
                setIsPremium(false);
              }
            })
            .catch(() => setIsPremium(false));
        }
      } else {
        setIsPremium(false);
      }
    };

    checkPremium();
  }, [user]);

  useEffect(() => {
    // Check online status immediately
    const checkOnlineStatus = () => {
      const isOnline = navigator.onLine;
      const currentStatus = offlineSync.getStatus();
      const shouldShow = !isOnline || currentStatus.pendingCount > 0;
      
      console.log('[OfflineBanner] Status check:', { 
        isOnline, 
        pendingCount: currentStatus.pendingCount, 
        shouldShow 
      });
      
      setSyncStatus({
        ...currentStatus,
        isOnline
      });
      setIsVisible(shouldShow);
    };

    // Initial check
    checkOnlineStatus();

    // Subscribe to offline sync status changes
    const unsubscribe = offlineSync.subscribe((status) => {
      const shouldShow = !status.isOnline || status.pendingCount > 0;
      console.log('[OfflineBanner] Sync status update:', { 
        isOnline: status.isOnline, 
        pendingCount: status.pendingCount, 
        shouldShow 
      });
      setSyncStatus(status);
      setIsVisible(shouldShow);
    });

    // Listen to browser online/offline events
    window.addEventListener('online', () => {
      console.log('[OfflineBanner] Browser online event');
      checkOnlineStatus();
    });
    window.addEventListener('offline', () => {
      console.log('[OfflineBanner] Browser offline event');
      checkOnlineStatus();
    });

    return () => {
      unsubscribe();
      window.removeEventListener('online', checkOnlineStatus);
      window.removeEventListener('offline', checkOnlineStatus);
    };
  }, []);

  // Update CSS variable for banner height
  useEffect(() => {
    const banner = document.querySelector('[data-offline-banner]') as HTMLElement;
    if (banner) {
      const height = banner.offsetHeight;
      document.documentElement.style.setProperty('--offline-banner-height', `${height}px`);
    } else {
      // Banner hidden, reset height
      document.documentElement.style.setProperty('--offline-banner-height', '0px');
    }
  }, [syncStatus.isOnline, syncStatus.pendingCount, hasImpersonationBanner]);

  const isOffline = !syncStatus.isOnline;
  const topOffset = hasImpersonationBanner ? '44px' : '0px';

  // Hide banner if not premium, or if online and no pending items
  if (!isPremium || !isVisible || (syncStatus.isOnline && syncStatus.pendingCount === 0)) {
    return null;
  }

  return (
    <div 
      data-offline-banner
      className="fixed left-0 right-0 z-40 bg-warning-500 dark:bg-warning-600 border-b-2 border-yellow-600 dark:border-yellow-700 shadow-lg"
      style={{ top: topOffset }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 min-h-[48px] flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {isOffline ? (
              <WifiOff className="w-5 h-5 text-warning-900 dark:text-warning-100 flex-shrink-0" />
            ) : (
              <Wifi className="w-5 h-5 text-warning-900 dark:text-warning-100 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-warning-900 dark:text-warning-100 leading-tight">
                {isOffline 
                  ? t('offline.bannerTitle')
                  : t('offline.bannerSyncing', { count: syncStatus.pendingCount, plural: syncStatus.pendingCount === 1 ? '' : 's' })
                }
              </p>
              {syncStatus.pendingCount > 0 && (
                <p className="text-xs text-warning-800 dark:text-warning-200 mt-0.5 leading-tight">
                  {t('offline.bannerPending', { 
                    count: syncStatus.pendingCount, 
                    plural: syncStatus.pendingCount === 1 ? '' : 's' 
                  })}
                </p>
              )}
            </div>
          </div>
          {isOffline && (
            <button
              onClick={() => setIsVisible(false)}
              className="flex-shrink-0 text-warning-900 dark:text-warning-100 hover:text-warning-950 dark:hover:text-warning-50 transition-colors p-1 rounded hover:bg-warning-400 dark:hover:bg-warning-700"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

